<template>
    <div>
        <DefaultCard title="Payments"
                     :is-busy="isBusy"
                     :error="error">
            <InputField :value.sync="searchQuery"
                        class="search" placeholder="Search" id="search">
            </InputField>
            <TableList class="mb-6"
                       :columns="paymentTableColumns"
                       :rows="searchedPayments"
                       empty-text="No Payments available"
                       :is-clickable= true @row-clicked="showPayment" v-if="!isMobile">
                <template slot="column_invoice"
                          slot-scope="{data}">
                    {{data.invoice || "---"}}
                </template>
                <template slot="column_provider"
                          slot-scope="{data}">
                    <span class="provider"> {{data.provider}} </span><span v-if="data.state" class="state"> {{data.state}} </span> <span v-if="!data.state||!data.provider">{{data.method}}</span>
                </template>
                <template slot="column_amount"
                          slot-scope="{data}">
                    {{data.currency + " " + data.amount}}
                </template>
                <template slot="column_createdAt"
                          slot-scope="{data}">
                    {{formatDate(data.createdAt)}}
                </template>
                <template slot="column_refundable"
                          slot-scope="{data}">
                    <span class="icon" :class="data.refundable ? 'yes' : 'no'"></span><span>{{ data.refundable ? "Yes" : "No" }}</span>
                </template>

            </TableList>

            <div v-else v-for="(entry, index) in searchedPayments" :key="index" class="card-item" @click="displayPayment(entry)">
                <div class="entry-row">
                    <label>{{ $t("payments.details.invoice") }}</label>
                    <div>
                        <span>{{ selectedPayment.invoice || "---" }}</span>
                    </div>
                </div>
                <div class="entry-row">
                    <label>{{ $t("payments.amount") }}</label>
                    <div>
                        <span>{{ entry.amount ? entry.currency + " " + entry.amount : "---" }}</span>
                    </div>
                </div>
                <div class="entry-row">
                    <label>{{ $t("payments.details.method") }}</label>
                    <div>
                        <span>{{ entry.provider }}{{ entry.state ? " " + entry.state : "" }}</span>
                    </div>
                </div>
                <div class="entry-row">
                    <label>{{ $t("payments.createdAt") }}</label>
                    <div>
                        <span>{{ formatDate(entry.createdAt)}}</span>
                    </div>
                </div>
                <div class="entry-row">
                    <label>{{ $t("payments.refundable") }}</label>
                    <div>
                        <span>{{ entry.refundable ? "Yes" : "No" }}</span>
                    </div>
                </div>
            </div>

        </DefaultCard>
        <TDSModal v-if="showModal" class="paymentModal" @close="closeModal" :hide-close-button=false :width="730">
            <template v-slot:header>{{ $t("payments." + modalView + ".title")}}</template>
            <template v-slot:body>
                <div class="error" v-if="modalError" v-html="modalError" />
                <div v-if="modalView === 'refund'" class="hint">
                    {{ $t("payments.details.review") }}
                </div>
                <div class="payment-info">
                    <div class="payment-info-item">
                        <label>{{ $t("payments.details.invoice") }}</label>
                        <div>
                            <span>{{ selectedPayment.invoice || "---" }}</span>
                        </div>
                    </div>
                </div>
                <div class="payment-info">
                    <div class="payment-info-item">
                        <label>{{ $t("payments.amount") }}</label>
                        <div>
                            <span>{{ selectedPayment.amount ? selectedPayment.currency + " " + selectedPayment.amount : "---" }}</span>
                        </div>
                    </div>
                    <div class="payment-info-item">
                        <label>{{ $t("payments.details.method") }}</label>
                        <div>
                            <span>{{ selectedPayment.provider }}{{ selectedPayment.state ? " " + selectedPayment.state : "" }}</span>
                        </div>
                    </div>
                    <div class="payment-info-item">
                        <label>{{ $t("payments.createdAt") }}</label>
                        <div>
                            <span>{{ formatDate(selectedPayment.createdAt)}}</span>
                        </div>
                    </div>
                    <div class="payment-info-item">
                        <label>{{ $t("payments.refundable") }}</label>
                        <div>
                            <span>{{ selectedPayment.refundable ? "Yes" : "No" }}</span>
                        </div>
                    </div>
                </div>
                <InputField v-if="modalView === 'refund'"
                            :placeholder="$t('payments.refund.2FAPlaceholder')"
                            class="mt-2"
                            :label="$t('payments.refund.2FA')"
                            :value.sync="twoFactor"
                            max-width="277"
                            type="text">
                </InputField>
                <div class="checkbox-group" v-if="modalView === 'refund'">
                    <TDSCheckbox class="mb-4 mt-4" v-if="selectedPayment.reducePackBalanceAllowed" :activated.sync="reducePackageBalance">
                        {{ $t("payments.refund.deductPackBalance") }}
                    </TDSCheckbox>
                    <TDSCheckbox class="mb-4 mt-4" v-if="selectedPayment.reduceDentAllowed" :activated.sync="reduceDent">
                        {{ $t("payments.refund.deductDent") }}
                    </TDSCheckbox>
                </div>

                <div v-if="modalView === 'sendReceipt'">
                    <InputField :label="$t('general.email')"
                                :validator-reg-exp="emailRegex"
                                class="mt-2"
                                :value.sync="email">
                    </InputField>
                </div>
                <div v-else class="mt-2 mb-3">
                    <a  @click.stop="modalView = 'sendReceipt'"
                        v-if="selectedPayment.hasReceipt">{{ $t("payments.details.sendReceipt") }}</a>
                    <a v-if="selectedPayment.hasReceipt"
                       :href="selectedPayment.receiptPdfLink"
                       :target="selectedPayment.receiptPdfLink ? '_blank' : 'self' "
                       class="ml-4" @click.stop="getReceiptAsPdf"
                       :disabled="isBusy">
                        <span v-if="selectedPayment.receiptPdfLink">
                            {{ $t("payments.details.downloadReceiptPdf") }}
                        </span>
                        <span v-else @click="getReceipt">
                            {{ $t("payments.details.getReceiptPdf") }}
                            </span>
                    </a>
                </div>
            </template>
            <template v-slot:footer>
                <div style="display: flex">
                    <div class="button-wrapper">
                        <TDSButton v-if="modalView === 'refund'"
                                   button-style="primary-red"
                                   @click="refund"
                                   :is-loading="isBusy"
                                   :inactive="!twoFactor">
                            {{$t('payments.refund.refundNow')}}
                        </TDSButton>
                    </div>
                    <div class="button-wrapper" v-if="modalView === 'sendReceipt'">
                        <TDSButton v-if="modalView === 'sendReceipt'"
                                   button-style="primary-red"
                                   @click="sendReceipt" :inactive="!email">
                            {{$t('payments.sendReceipt.title')}}
                        </TDSButton>
                    </div>
                    <div class="button-wrapper" v-else>
                        <TDSButton
                                   display="inline"
                                   button-style="primary-red"
                                   @click="closeModal" class="done">
                            Done
                        </TDSButton>
                    </div>
                    <div class="button-wrapper">
                        <TDSButton v-if="selectedPayment.refundable && modalView === 'details'"
                                   display="inline"
                                   button-style="primary"
                                   @click="goToRefund">{{$t('payments.details.toRefund')}}
                        </TDSButton>
                    </div>
                </div>
            </template>
        </TDSModal>
    </div>
</template>

<script lang="ts">
import {Component, Watch} from "vue-property-decorator";
import DefaultLayout from "../layouts/DefaultLayout.vue";
import TDSError from "@/components/common/TDSError.vue";
import TDSSpinner from "@/components/common/TDSSpinner.vue";
import InputField from "@/components/common/InputField.vue";
import DefaultCard from "@/components/partials/DefaultCard.vue";
import {VueClassTemplate} from "@/util/VueClassTemplate";
import RestrictUserForm from "@/components/partials/Restriction/RestrictUserForm.vue";
import TDSButton from "@/components/common/TDSButton.vue";
import FlagUserForm from "@/components/partials/UserFlags/FlagUserForm.vue";

import {PaymentRecord} from "@/interfaces/entities/PaymentRecord";
import TableList, {TableListColumnConfig} from "@/components/common/TableList.vue";
import moment from "moment";
import TDSModal from "@/components/common/TDSModal/TDSModal.vue";
import BubbleTabs from "@/components/partials/Navigation/BubbleTabs.vue";
import {SendReceiptRequest} from "@/interfaces/httpRequests/SendReceiptRequest";
import TDSCheckbox from "@/components/common/TDSCheckbox.vue";

@Component({
    components: {
        TDSCheckbox,
        BubbleTabs,
        TDSModal,
        TableList,
        DefaultLayout, TDSError, TDSSpinner, InputField, DefaultCard, RestrictUserForm, TDSButton, FlagUserForm}
})

export default class Payments extends VueClassTemplate{
    private userId: string = this.$route.params.userId;
    private payments: Array<PaymentRecord> = [];
    private searchedPayments: Array<PaymentRecord> = [];
    private showModal: boolean = false;
    private modalView: string = "details";
    private email: string = "";
    private emailRegex: RegExp = new RegExp(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);
    private searchQuery: string = "";
    private twoFactor: string = "";
    private modalError: string = "";
    private reducePackageBalance: boolean = false;
    private reduceDent: boolean = false;
    private selectedPayment: PaymentRecord = {
        invoice: "",
        method: "",
        amount:"",
        currency:"",
        createdAt:"",
        refundable: false,
        txnId:"",
        reduceDentAllowed: false,
        reducePackBalanceAllowed: false,
        hasReceipt: false,
        paymentId: 0,
        provider: "",
        receiptId: 0,
        state: "",
        requestReducePackBalance: false
    };

    @Watch("error")
    errorChanged() {
        console.log("error changed: ", this.error);
    }
    @Watch("searchQuery")
    queryChanged(){
        this.handleSearch();
    }

    handleSearch(){
        this.searchedPayments = this.payments;
        const regexp = new RegExp(this.searchQuery, "i");
        this.searchedPayments = this.searchedPayments.filter( (x: PaymentRecord) => this.getMatch(Object.values(x), regexp));
    }

    getMatch(array: Array<any>, regex: RegExp){
        let found: boolean = false;
        array.forEach(function(e: any){
            if (regex.test(e)){
                found = true;
            }
        }
        );
        return found;
    }

    async getReceipt(){
        this.startProgress("getReceipt");
        try {
            const txnId = this.selectedPayment.txnId;
            const response: string = await this.$store.dispatch("GET_INVOICE", txnId);
            console.log(response);
            console.log("Get Receipt!");
        }
        catch (e) {
            this.error = "Error while fetching receipt: "+e.message;
            this.$toastr.error("Error while fetching receipt: "+e.message);
        }
        this.endProgress("getReceipt");
    }

    async sendReceipt() {
        this.startProgress("sendReceipt");
        try {
            console.log("[PaymentsPage] Receipt email:", this.email);
            const payload: SendReceiptRequest ={
                txnId: this.selectedPayment.txnId,
                email: this.email
            };
            await this.$store.dispatch("SEND_INVOICE", payload);
            console.log(payload);
        } catch (e) {
            this.error = "Error while sending receipt: " +e.message;
            this.$toastr.error("Error while sending receipt: "+e.message);
        }
        this.endProgress("sendReceipt");
        this.$toastr.success(this.$t("payments.sendReceipt.successful", {email: this.email})).then(() => {
            this.showModal = false;
        });
    }

    showPayment(event: number) {
        // eslint-disable-next-line @typescript-eslint/typedef
        this.selectedPayment= this.payments.find(e => e.id === event)!;
        this.reducePackageBalance= this.selectedPayment.reducePackBalanceAllowed;
        this.reduceDent = this.selectedPayment.reduceDentAllowed;
        this.$set(this, "modalView", "details");
        this.email = "";
        this.showModal = true;
    }

    displayPayment(selected: PaymentRecord) {
        this.selectedPayment= selected;
        this.reducePackageBalance= this.selectedPayment.reducePackBalanceAllowed;
        this.reduceDent = this.selectedPayment.reduceDentAllowed;
        this.$set(this, "modalView", "details");
        this.email = "";
        this.showModal = true;
    }

    get paymentTableColumns(): TableListColumnConfig{
        return {
          invoice: {label: "Invoice", sortable: true},
          provider: {label: "Type/Method", sortable: true},
          amount: {label: "Amount", sortable: true},
          createdAt: {label: "Created At", sortable: true},
          refundable: {label: "Refundable", sortable: true}
        };
    }

    closeModal(){
        this.showModal = false;
    }

    goToRefund(){
        this.modalView = "refund";
    }


    async init(){
        this.startProgress("loading");
        await this.loadPayments();
        this.endProgress("loading");
    }

    async loadPayments(){
        this.startProgress("loadingPayments");
        try {
            const response: Array<PaymentRecord> = await this.$store.dispatch("GET_PAYMENTS",
                {
                    userId: this.userId,
                    paymentBody: {

                    }
                }
            );
            this.$set(this, "payments", response.sort((itemA: PaymentRecord, itemB: PaymentRecord) => {
                if (itemA.createdAt < itemB.createdAt) return 1;
                if (itemA.createdAt > itemB.createdAt) return -1;
                return 0;
            }));
        }  catch (e){
            this.error = "<b>Couldn't load the Payment History: </b><br>" +e.message;
        }
        this.payments.forEach((element: PaymentRecord, index: number) => {element.id = index+1});
        this.payments.forEach((element: PaymentRecord) =>{element.amount = parseFloat(element.amount).toString()});
        this.searchedPayments = this.payments;
        this.endProgress("loadingPayments");
    }

    formatDate(date: string){
        let parsedDate: number = 0;
        if(!date) return "";
        if(typeof date === "string") {
            if(date.includes("[UTC]")) date = date.replace("[UTC]", "");
            parsedDate = Date.parse(date);
        }
        return (moment(parsedDate)).format("YYYY-MM-DD HH:mm");
    }

    async refund() {
        this.startProgress("refunding");
        try {
            await this.$store.dispatch("REFUND_PAYMENT", {
                txnId: this.selectedPayment.txnId,
                reason: "",
                reduceDentAllowed: this.reduceDent,
                reducePackBalanceAllowed: this.reducePackageBalance,
                twoFactorCode: this.twoFactor
            });
            this.$toastr.success(this.$t("payments.refund.successful", {invoice: this.selectedPayment.invoice})).then(() => {
                this.showModal = false;
                this.loadPayments();
            });

        } catch (e) {
            console.log("Error: ", e.response);
            if (e.response?.status === 401) this.modalError = "The 2FA code was not correct.";
            else {
                const details = (e.response.data && e.response.data.message) ? JSON.stringify(e.response.data.message) : JSON.stringify(e.response.data);
                this.modalError = "Error while requesting refund:<br/>" + details;
            }
            this.$toastr.error("Error while trying to refund", 5000, true);
        }
        this.endProgress("refunding");
    }

}
</script>

<style lang="scss" scoped>
@import "../../styles/variables";
.done{
    @media only screen and (max-width: $breakPoint){
        min-width: 100px
    }
}
.button-wrapper{
    margin-right: 0.5rem;
}
.provider {
    text-transform: capitalize;
}
.state {
    padding-left: 4px;
    text-transform: lowercase;
}
.error {
    word-break: break-all;
    color: #FA2922;
}
.icon {
    background: white url("../../assets/icons/close-circle-dent-red.svg") no-repeat left center;
    background-size: 18px auto;
    height: 18px;
    width: 18px;
    border-radius: 50%;
    float: left;
    margin-right: 4px;

    &.yes {
        background-image: url("../../assets/icons/check-circle-green.svg");
    }
}
.search {
    margin-bottom:10px;
    width: 197px;
    margin-right: 0;
    margin-left: auto;
    background-image: url("../../assets/icons/search-grey71.svg");

    ::after{
        background-color: #FA2922;
        background-size: 16px 16px;

    }

    &::before {
        content: "";
        display: block;
        position: absolute;
        height: 28px;
        width: 28px;
        background: transparent url("../../assets/icons/search-grey71.svg") no-repeat center;
        background-size: 16px 16px;
    }

    ::v-deep input {
        //@include fontBody();
        width: 197px;
        height: 28px;
        font-size: 14px;
        background: $greyShade2;
        border-radius: 3px 3px 3px 3px;
        margin-right: 0 !important;
        padding-left: 30px;
        border: none;
    }
}


.payments-infos {
    ///deep/ .table .content-row img{
    //  max-height: 28px;
    //}
    .icon {
        background: white url("../../assets/icons/close-circle-dent-red.svg") no-repeat left center;
        background-size: 18px auto;
        height: 18px;
        width: 18px;
        border-radius: 50%;
        float: left;
        margin-right: 4px;

        &.yes {
            background-image: url("../../assets/icons/check-circle-green.svg");
        }
    }
    .provider {
        text-transform: capitalize;
    }
    .state {
        padding-left: 4px;
    }
    .refund-link {
        color: $fullRed;
        text-decoration: underline;
        text-transform: none;
    }
}

.modal-container {
    a {
        color: $fullRed;
        text-decoration: underline;
        margin-right: 10px;
        cursor: pointer;
        &:hover {
            color: $fullRed;
        }
    }

    .hint {
        color: $greyShade4;
        margin-bottom: 16px;
    }

    .payment-info {
        display: flex;
        flex-wrap: wrap;

        .payment-info-item {
            display: flex;
            flex-direction: column;
            margin: 0 30px 16px 0;

            label {
                @include fontCaption();
                display: block;
                margin-bottom: 4px;
            }

            span {
                color: $fullGrey;
            }

            span, a {
                text-transform: capitalize;
                font-size: 12px;
                line-height: 1;
            }

            > div {
                display: flex;
            }
        }
    }
}
.card-item {
    //display: flex;
    margin-bottom: 5px;
    justify-content: start;
    flex-grow: 1;
    margin-top: 8px;
    padding-bottom: 8px;
    .entry-row{
        display: flex;
        justify-content: space-between;
    }
    > div{
        padding-right: 5px;
    }
    .content{
        flex-grow: 1;
        >div{
            display: flex;
            >div{
                flex-grow: 2;
            }
        }
    }
    &:not(last-child) {
        border-bottom: 1px solid $greyShade3
    }
}

</style>
